import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faLinkSlash } from "@fortawesome/free-solid-svg-icons/faLinkSlash";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faRotate } from "@fortawesome/free-solid-svg-icons/faRotate";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { WarningModal } from "../../../../components/WarningModal";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { Tooltip } from "../../../../utils/local-antd";
import { useIntegrationContext } from "../../context/integration-context";
import { IntegrationFacebookMetaFormsDrawer } from "../IntegrationFacebookMetaFormsDrawer";
import { IntegrationSection } from "../IntegrationSection";
import { useFacebookMetaConnect } from "../useFacebookMetaConnect";

const StyledButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  color: ${({ theme }) => theme.main};
`;

export const IntegrationFacebookMetaSection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setAddModalFacebookMetaVisible, setFacebookMetaEditFormId } =
    useIntegrationContext();
  const { connect, connecting } = useFacebookMetaConnect();

  const [formsDrawerOpen, setFormsDrawerOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectFacebookMetaIntegration,
  );

  if (!integration) {
    return null;
  }

  const needsReconnect = Boolean(integration.config.needsReconnect);

  const onDisconnect = async () => {
    try {
      setLoading(true);

      await dispatch(
        integrationActions.disconnectIntegration({
          userId,
          integrationId: integration._id,
        }),
      ).unwrap();
      await dispatch(integrationActions.getUserIntegrations({ userId }));

      setDisconnectOpen(false);
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IntegrationSection
      enabled={integration.status.enabled && !needsReconnect}
      icon={faFacebook}
      iconColor="facebook"
    >
      <Flex gap="s" alignItems="center" justifyContent="space-between" flex={1}>
        <Flex gap="m" alignItems="center">
          <Text>{t("integration.facebook-meta.modal.title")}</Text>

          <Text color={needsReconnect ? "danger" : "darkerSub"}>
            {needsReconnect
              ? t("integration.facebook-meta.section.needsReconnect")
              : t("integration.facebook-meta.section.formsCount", {
                  count: integration.config.forms?.length || 0,
                })}
          </Text>
        </Flex>

        <Flex gap="s" alignItems="center">
          <Tooltip
            title={t("integration.facebook-meta.section.disconnect.tooltip")}
          >
            <StyledButton
              icon={faLinkSlash}
              onClick={() => setDisconnectOpen(true)}
              iconWidth="2x"
            />
          </Tooltip>

          <Tooltip title={t("common.reconnect")}>
            <StyledButton
              icon={faRotate}
              onClick={connect}
              loading={connecting}
              iconWidth="2x"
            />
          </Tooltip>

          <Tooltip title={t("integration.facebook-meta.section.addForm")}>
            <StyledButton
              icon={faPlus}
              onClick={() => {
                setFacebookMetaEditFormId(null);
                setAddModalFacebookMetaVisible(true);
              }}
              disabled={needsReconnect}
              iconWidth="2x"
            />
          </Tooltip>

          <Tooltip title={t("integration.facebook-meta.drawer.title")}>
            <StyledButton
              icon={faEye}
              onClick={() => setFormsDrawerOpen(true)}
              iconWidth="2x"
            />
          </Tooltip>
        </Flex>
      </Flex>

      <IntegrationFacebookMetaFormsDrawer
        open={formsDrawerOpen}
        onClose={() => setFormsDrawerOpen(false)}
      />

      <WarningModal
        isOpen={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={onDisconnect}
        loading={loading}
        title={t("integration.facebook-meta.section.disconnect.title")}
        description={t(
          "integration.facebook-meta.section.disconnect.description",
        )}
      />
    </IntegrationSection>
  );
};
